import * as React from "react";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, ArrowUpRight, Check, Loader2, Lock } from "lucide-react";

import { AppShell } from "@/components/layout/app-shell";
import { ApplyLateFeeDialog } from "@/components/apply-late-fee-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useGetFeesForInvoice,
  useGetInvoice,
  useGetWorkflowFeesSettings,
  usePreviewFee,
} from "@/lib/api/generated/default/default";
import { InvoiceStatus } from "@/lib/api/generated/model";
import { browserLocale, formatMoney } from "@/lib/late-fee";
import { buildPaymentFeeCreateIn, settingsToPolicyDraft } from "@/lib/fee-mapping";
import { xeroInvoiceUrl } from "@/lib/xero-connect";
import { cn } from "@/lib/utils";

/** The applied late-fee invoice's own status in Xero — draft until someone approves it there. */
function appliedFeeBadge(status: InvoiceStatus): {
  label: string;
  variant: "success" | "gold" | "warning" | "muted";
} {
  switch (status) {
    case InvoiceStatus.draft:
      return { label: "Draft in Xero", variant: "gold" };
    case InvoiceStatus.posted:
      return { label: "Approved in Xero", variant: "success" };
    case InvoiceStatus.overdue:
      return { label: "Approved in Xero", variant: "warning" };
    case InvoiceStatus.paid:
      return { label: "Paid", variant: "success" };
    case InvoiceStatus.cancelled:
      return { label: "Voided in Xero", variant: "muted" };
    default:
      return { label: "In Xero", variant: "muted" };
  }
}

export const Route = createFileRoute("/invoices/$id")({
  head: ({ params }) => ({
    meta: [
      { title: `Invoice ${params.id} — Treasure` },
      {
        name: "description",
        content: `Original invoice ${params.id} and its late-fee twin, side by side.`,
      },
    ],
  }),
  component: InvoiceDetail,
  notFoundComponent: InvoiceNotFound,
});

function InvoiceDetail() {
  const { id } = Route.useParams();
  const locale = browserLocale();

  // All hooks live here, unconditionally, before any early return below —
  // calling hooks after a conditional `return`/`throw` is a Rules-of-Hooks
  // violation: on the render that takes the early exit, those hooks simply
  // never fire, so the hook count/order changes between renders and React
  // crashes ("Rendered more/fewer hooks than during the previous render").
  const invoiceQuery = useGetInvoice(id);
  const settingsQuery = useGetWorkflowFeesSettings({ workflow_id: null });
  const previewFee = usePreviewFee();
  const [applyOpen, setApplyOpen] = React.useState(false);

  const invoice = invoiceQuery.data;
  const eligible = invoice?.fee === "eligible";
  const isApplied = invoice?.fee === "applied";

  // The invoice object only carries a `fee` status enum, not the actual
  // applied fee's amount/number/etc. — that's a separate resource.
  const feesForInvoiceQuery = useGetFeesForInvoice(id, { query: { enabled: isApplied } });

  const policy = React.useMemo(
    () => settingsToPolicyDraft(settingsQuery.data),
    [settingsQuery.data],
  );

  // The fee breakdown is only ever the real backend preview — never a
  // client-side guess, since it accounts for things like org timezone.
  const previewPayload = React.useMemo(
    () =>
      invoice && eligible
        ? buildPaymentFeeCreateIn({
            invoiceId: invoice.id,
            customerId: invoice.customer.id,
            invoiceNumber: invoice.number,
            policy,
          })
        : null,
    [invoice, eligible, policy],
  );

  React.useEffect(() => {
    if (previewPayload) previewFee.mutate({ data: previewPayload });
  }, [previewPayload]);

  if (invoiceQuery.isLoading) {
    return (
      <AppShell>
        <div className="grid gap-6 lg:grid-cols-2">
          <Skeleton className="h-96 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </AppShell>
    );
  }
  if (invoiceQuery.isError || !invoice) throw notFound();

  const money = (n: number, c = invoice.currency) => formatMoney(n, c, locale);

  const applied = isApplied;
  const notEligible = invoice.fee === "not_eligible";

  const appliedFee = feesForInvoiceQuery.data;

  const preview = previewFee.data;
  const total = preview?.reduce((sum, f) => sum + Number(f.total_amount), 0) ?? 0;

  const handleApplied = () => {
    invoiceQuery.refetch();
    feesForInvoiceQuery.refetch();
  };

  return (
    <AppShell>
      <div className="mb-4">
        <Button asChild variant="ghost" size="sm" className="-ml-2">
          <Link to="/invoices">
            <ArrowLeft className="size-4" /> Back to all invoices
          </Link>
        </Button>
      </div>

      {/* Header */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl leading-tight text-foreground">
            <span className="font-numeric">{invoice.number}</span>{" "}
            <span className="text-muted-foreground">·</span>{" "}
            <span className="text-foreground">{invoice.customer.name ?? "—"}</span>
          </h1>
        </div>
        <Badge variant="warning" size="pill">
          {invoice.days_overdue ?? 0} days overdue
        </Badge>
      </div>

      {/* Two columns */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* LEFT — Original */}
        <Card className="p-6">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <div className="mt-0.5 font-display text-lg text-foreground">Original invoice</div>
            </div>
            <span
              className="inline-flex items-center gap-1 rounded-full bg-hairline px-2.5 py-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
              title="This card is read-only"
            >
              <Lock className="size-3" /> Original
            </span>
          </div>

          <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
            <Row
              label="Invoice no."
              value={<span className="font-numeric">{invoice.number}</span>}
            />
            <Row label="Customer" value={invoice.customer.name ?? "—"} />
            <Row label="Issued" value={invoice.posted_at ? formatDate(invoice.posted_at) : "—"} />
            <Row label="Due" value={formatDate(invoice.due)} />
          </dl>

          <div className="mt-4 overflow-hidden rounded-md border border-hairline">
            <div className="flex items-center justify-between bg-secondary/60 px-4 py-3">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Total due
              </span>
              <span className="font-display font-numeric text-2xl font-semibold text-foreground">
                {money(invoice.amount_due)}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
            <Button asChild variant="secondary" size="sm">
              <a
                href={xeroInvoiceUrl(invoice.external_id)}
                target="_blank"
                rel="noopener noreferrer"
              >
                View in Xero <ArrowUpRight className="size-3.5" />
              </a>
            </Button>
          </div>
        </Card>

        {/* RIGHT — Late-fee invoice */}
        <Card className="p-6">
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <div className="mt-0.5 flex items-center gap-2 font-display text-lg text-foreground">
                {applied ? (
                  appliedFee ? (
                    "Late fee invoice"
                  ) : (
                    <span className="text-sm font-normal text-muted-foreground">Loading…</span>
                  )
                ) : (
                  "Late fee due"
                )}
              </div>
            </div>
            {applied ? (
              appliedFee?.status ? (
                <Badge
                  variant={appliedFeeBadge(appliedFee.status).variant}
                  size="pill"
                  className="gap-1"
                >
                  <Check className="size-3" /> {appliedFeeBadge(appliedFee.status).label}
                </Badge>
              ) : (
                <Badge variant="muted" size="pill">
                  Loading…
                </Badge>
              )
            ) : eligible ? (
              <Badge variant="gold" size="pill">
                Potential
              </Badge>
            ) : (
              <Badge variant="muted" size="pill">
                Not eligible
              </Badge>
            )}
          </div>

          <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
            <Row
              label="Invoice no."
              value={
                <span className="font-numeric">{applied ? (appliedFee?.number ?? "—") : "—"}</span>
              }
            />
            <Row label="Customer" value={invoice.customer.name ?? "—"} />
            <Row
              label="Issued"
              value={applied && appliedFee?.posted_at ? formatDate(appliedFee.posted_at) : "—"}
            />
            <Row label="Due" value={applied && appliedFee ? formatDate(appliedFee.due) : "—"} />
          </dl>

          {/* Total */}
          {eligible && (
            <div className="mt-4 overflow-hidden rounded-md border border-hairline">
              {previewFee.isPending ? (
                <div className="flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground">
                  <Loader2 className="size-4 animate-spin" /> Calculating fee…
                </div>
              ) : previewFee.isError ? (
                <div className="px-4 py-6 text-sm text-destructive">
                  Couldn't calculate the fee. Try again.
                </div>
              ) : (
                <>
                  {preview?.map((line, i) => (
                    <LineRow
                      key={i}
                      label={line.description}
                      detail=""
                      amount={money(Number(line.total_amount))}
                    />
                  ))}
                  <div className="flex items-center justify-between bg-gold-tint/50 px-4 py-3">
                    <span className="text-xs font-medium uppercase tracking-wide text-gold-foreground">
                      Total late fee
                    </span>
                    <span className="font-display font-numeric text-2xl font-semibold text-gold-foreground">
                      {money(total)}
                    </span>
                  </div>
                </>
              )}
            </div>
          )}
          {applied && (
            <div className="mt-4 overflow-hidden rounded-md border border-hairline">
              {feesForInvoiceQuery.isLoading ? (
                <div className="flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground">
                  <Loader2 className="size-4 animate-spin" /> Loading late fee…
                </div>
              ) : feesForInvoiceQuery.isError || !appliedFee ? (
                <div className="px-4 py-6 text-sm text-destructive">
                  Couldn't load the applied late fee.
                </div>
              ) : (
                <div className="flex items-center justify-between bg-gold-tint/50 px-4 py-3">
                  <span className="text-xs font-medium uppercase tracking-wide text-gold-foreground">
                    Total late fee
                  </span>
                  <span className="font-display font-numeric text-2xl font-semibold text-gold-foreground">
                    {money(appliedFee.total_amount, appliedFee.currency)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Banner */}
          {eligible && (
            <div className="mt-4 rounded-md border border-primary/20 bg-primary-tint px-3 py-2 text-xs text-primary">
              Not raised yet — this is what Treasure would create.
            </div>
          )}
          {notEligible && (
            <div className="mt-4 rounded-md border border-hairline bg-secondary px-3 py-2 text-xs text-muted-foreground">
              This invoice doesn't meet your late-fee policy yet.
            </div>
          )}

          {/* Actions */}
          <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
            <div className="flex flex-wrap items-center gap-2">
              {eligible && (
                <Button size="sm" onClick={() => setApplyOpen(true)}>
                  Apply late fee
                </Button>
              )}
              {/* Deep links to the late-fee document itself in Xero, not the
                  original invoice — so it only makes sense once a fee exists. */}
              {applied && appliedFee?.external_id && (
                <Button asChild variant="secondary" size="sm">
                  <a
                    href={xeroInvoiceUrl(appliedFee.external_id)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View in Xero <ArrowUpRight className="size-3.5" />
                  </a>
                </Button>
              )}
              {notEligible && (
                <Button size="sm" disabled>
                  Apply late fee
                </Button>
              )}
            </div>
          </div>
        </Card>
      </div>
      <ApplyLateFeeDialog
        invoice={invoice}
        policy={policy}
        locale={locale}
        open={applyOpen}
        onOpenChange={setApplyOpen}
        onApplied={handleApplied}
      />
    </AppShell>
  );
}

function Row({
  label,
  value,
  className,
}: {
  label: string;
  value: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{value}</dd>
    </div>
  );
}

function LineRow({
  label,
  detail,
  amount,
  amountClass,
}: {
  label: string;
  detail: string;
  amount: React.ReactNode;
  amountClass?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-hairline px-4 py-3 last:border-0">
      <div className="min-w-0">
        <div className="text-sm font-medium text-foreground">{label}</div>
        {detail && <div className="truncate text-xs text-muted-foreground">{detail}</div>}
      </div>
      <div className={cn("shrink-0 text-right font-numeric text-sm text-foreground", amountClass)}>
        {amount}
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function InvoiceNotFound() {
  const { id } = Route.useParams();
  return (
    <AppShell>
      <Card className="mx-auto max-w-lg p-8 text-center">
        <div className="font-display text-xl">We couldn't find {id}</div>
        <p className="mt-2 text-sm text-muted-foreground">
          It may have been removed, or the link is off by a digit.
        </p>
        <div className="mt-4">
          <Button asChild>
            <Link to="/invoices">Back to all invoices</Link>
          </Button>
        </div>
      </Card>
    </AppShell>
  );
}
